// CtaButtonSection.js — Sección de Call to Action
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { buildBg } from '../bgHelpers';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(20,182,203,0.3); }
  50% { box-shadow: 0 8px 30px rgba(20,182,203,0.5); }
`;

const Wrapper = styled.section`
  padding: ${p => p.$padding || '4rem 2rem'};
  background: ${p => p.$bg || '#f8fafc'};
  text-align: ${p => p.$align || 'center'};
`;

/* Con `ancho_maximo` el contenido se alinea con el resto de la pagina en vez de
   pegarse al borde de la pantalla. Sin la clave, la seccion va como siempre. */
const Inner = styled.div`
  max-width: ${p => p.$maxWidth};
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: 38px;
  color: ${p => p.$color || '#0f355b'};
  font-family: 'Myriad Pro', sans-serif;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.div`
  font-size: 19px;
  color: ${p => p.$color || '#64748b'};
  font-family: 'Myriad Pro', sans-serif;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

/* El botón admite un aspecto "de contorno" (fondo transparente + borde) y
   apagar la animación, para cuando va sobre un fondo oscuro y debe ser
   discreto. Sin esas claves queda exactamente como siempre. */
const Btn = styled.button`
  background: ${p => p.$bg || '#14b6cb'};
  color: ${p => p.$color || '#fff'};
  padding: ${p => p.$padding || '12px 40px'};
  border-radius: ${p => p.$radio || '10px'};
  font-weight: ${p => p.$peso || 'bold'};
  font-size: ${p => p.$tamano || '19px'};
  font-family: 'Myriad Pro', sans-serif;
  border: ${p => (p.$borde ? `1px solid ${p.$borde}` : 'none')};
  cursor: pointer;
  transition: 0.3s;
  ${p => !p.$sinAnimacion && css`animation: ${pulse} 3s infinite;`}
  &:hover { transform: translateY(-3px); animation: none; }
`;

const CtaButtonSection = ({ config, openLogin, openRegister, tl }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    const action = config.accion || 'registro';
    if (action === 'login') { openLogin?.(); return; }
    if (action === 'registro') { openRegister?.(); return; }
    const destino = action === 'url' ? (config.url || '') : (action.startsWith('http') ? action : '');
    if (!destino) return;
    // Una direccion del propio sitio se abre en la misma pestana —abrirla en otra
    // era desconcertante—; las externas siguen yendo a una nueva. Con
    // `nueva_pestana` se fuerza el comportamiento anterior.
    const externa = /^https?:\/\//i.test(destino);
    if (!externa && config.nueva_pestana !== true) navigate(destino);
    else window.open(destino, '_blank', 'noopener');
  };

  const contenido = (
    <>
      {config.titulo && <Title $color={config.color_titulo} dangerouslySetInnerHTML={{ __html: tl(config.titulo) }} />}
      {config.subtitulo && <Subtitle $color={config.color_subtitulo} dangerouslySetInnerHTML={{ __html: tl(config.subtitulo) }} />}
      <Btn
        $bg={config.boton_transparente ? 'transparent' : config.color_boton}
        $color={config.color_texto_boton}
        $borde={config.color_borde_boton}
        $padding={config.padding_boton}
        $radio={config.radio_boton}
        $tamano={config.tamano_boton}
        $peso={config.peso_boton}
        $sinAnimacion={config.sin_animacion === true}
        onClick={handleClick}
      >
        {tl(config.texto_boton, 'Comenzar ahora')}
      </Btn>
    </>
  );

  return (
    <Wrapper
      $bg={buildBg(config, '#f8fafc')}
      $padding={config.padding}
      $align={config.alineacion}
    >
      {config.ancho_maximo ? <Inner $maxWidth={config.ancho_maximo}>{contenido}</Inner> : contenido}
    </Wrapper>
  );
};

export default CtaButtonSection;
